import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Event } from "../types/events";
import { Calendar, Trophy } from "lucide-react";
import dreamWorldProvider from "../DreamWorldProvider";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import EventSubmissions from "../components/events/EventSubmissions";

const EventDetailPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;

    const fetchEvent = async () => {
      try {
        const eventData = await dreamWorldProvider.getEvent(id);
        setEvent(eventData);
      } catch (error) {
        console.error("Error fetching event: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-lg">Event not found.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-8"
            >
                <h1 className="text-4xl font-bold mb-2">{event.title} - Recap</h1>
                <p className="text-lg text-gray-400">{event.description}</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-800 p-4 rounded-lg">
                    <h2 className="text-2xl font-bold text-purple-400">Winners</h2>
                    {/* Placeholder for winners */}
                    <p className="text-gray-300 mt-2">Winners will be announced soon.</p>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                    <h2 className="text-2xl font-bold text-yellow-400">Submissions</h2>
                    <p className="text-5xl font-bold mt-2">{event.submissionCount}</p>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-6 h-6 text-purple-400" />
                    <h3 className="text-xl font-semibold text-white">Prizes</h3>
                  </div>
                  <p className="text-purple-300 font-medium">
                    {event.prizes.map(p => p.reward).join(' • ')}
                  </p>
                </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-white mb-4">Submissions</h2>
                <EventSubmissions eventId={event.id} />
            </div>
        </div>
    </div>
  );
};

export default EventDetailPage;
